import { useEffect } from 'react';
import type { BrowserAgentComponent } from 'api/types';
import { getOrCreatePusher } from 'components/App/hooks/useInitPusher';
import { useUpdateConversation } from 'components/App/hooks';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setStreaming } from 'reducers/workflows';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { PUSHER_ENCRYPTED_CHANNEL_PREFIX } from 'components/App/consts';

interface BrowserAgentStepProps {
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: BrowserAgentComponent;
}

export const BrowserAgentStep = ({
  setStepSent,
  widgetComponent,
}: BrowserAgentStepProps) => {
  const dispatch = useAppDispatch();
  const updateConversation = useUpdateConversation();
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);

  useEffect(() => {
    if (widgetComponent.isSent || !isCurrentTabPrimary) {
      return;
    }

    const channelId = widgetComponent.component_fields.channel_id;
    const encryptedChannelId = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}${channelId}`;

    const pusher = getOrCreatePusher();

    dispatch(setStreaming(true));

    const channel = pusher.subscribe(channelId);
    const encryptedChannel = pusher.subscribe(encryptedChannelId);

    let hasResponded = false;

    const handleSuccess = () => {
      if (hasResponded) return;
      hasResponded = true;
      dispatch(setStreaming(false));
      setStepSent();
      updateConversation({}, { isContinueAutoflowIntent: true });
    };

    const handleError = () => {
      if (hasResponded) return;
      hasResponded = true;
      dispatch(setStreaming(false));
      setStepSent();
      updateConversation({}, { isContinueAutoflowIntent: true });
    };

    channel.bind('browser-agent-success', handleSuccess);
    channel.bind('browser-agent-error', handleError);

    encryptedChannel.bind('browser-agent-success', handleSuccess);
    encryptedChannel.bind('browser-agent-error', handleError);

    return () => {
      channel.unbind_all();
      encryptedChannel.unbind_all();
      pusher.unsubscribe(channelId);
      pusher.unsubscribe(encryptedChannelId);
      dispatch(setStreaming(false));
    };
  }, [
    dispatch,
    isCurrentTabPrimary,
    setStepSent,
    updateConversation,
    widgetComponent,
  ]);

  return null;
};
